import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useReportData } from '@/hooks/useReportData';
import { useUserData } from '@/hooks/useUserData';
import Header from '../components/Header';
import { FloatingNav } from '../components/report/FloatingNav';
import { ArrowLeft, Monitor } from 'lucide-react';
import DISCResultsView from '../components/report/DISCResults';
import LoadingScreen from '@/components/common/LoadingScreen';
import ReportErrorBoundary from '@/components/report/ReportErrorBoundary';
import { ReportHeader } from '@/components/report/ReportHeader';
import { ReportContent } from '@/components/report/ReportContent';
import { Navigate } from 'react-router-dom';

type TabType = 'general' | 'self-assessment' | 'disc';

export default function Report() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { userData, loading: userLoading } = useUserData();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const { reportData, loading: reportLoading, error } = useReportData();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // Redirect to sign in if not authenticated
  if (!currentUser && !userLoading && !reportLoading) {
    return <Navigate to="/signin" />;
  }

  if (userLoading || reportLoading) {
    return <LoadingScreen />;
  }

  if (error || !reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header 
          onLogout={handleLogout}
          onProfileClick={() => navigate('/profile')}
          userData={userData}
        />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg max-w-md text-center">
            <p className="mb-4">{error || 'Failed to load report data'}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-primary hover:text-primary-hover underline"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ReportErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        {/* Small Screen Message */}
        <div className="hidden max-[700px]:flex fixed inset-0 z-50 bg-white flex-col items-center justify-center p-6 text-center">
          <Monitor className="w-16 h-16 text-primary mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Larger Screen Required
          </h2>
          <p className="text-gray-600 max-w-md">
            To ensure the best experience and fully maximize the value of your report, please access it from a larger screen device.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-primary text-dark px-6 py-2 rounded-lg hover:bg-primary-hover transition-colors"
          >
            Return to Dashboard
          </button>
        </div>

        <Header 
          onLogout={handleLogout}
          onProfileClick={() => navigate('/profile')}
          userData={userData}
        />
        <div className="hidden min-[701px]:block">
          <FloatingNav activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        
        <main className="py-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </button>

            <div className="bg-white rounded-lg shadow">
              <div className="hidden min-[701px]:block">
                <ReportHeader 
                  activeTab={activeTab} 
                  onTabChange={setActiveTab} 
                />
                
                <ReportContent 
                  activeTab={activeTab}
                  reportData={reportData}
                />
              </div>
            </div>
          </div>
        </main>
      </div>
    </ReportErrorBoundary>
  );
}